import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from './AuthContext';
import { getActiveAnnouncement } from '@services/announcementService';

const AnnouncementContext = createContext();

export const useAnnouncement = () => useContext(AnnouncementContext);

const KEY_PREFIX = 'comhub.announcementDismissed';

// El descarte va en localStorage (sobrevive al logout, para no dar la lata en cada
// login) pero es por navegador, así que la clave incluye el usuario: sin eso, en un
// navegador compartido el segundo usuario heredaría el descarte del primero.
const dismissKey = (userId, version) => `${KEY_PREFIX}.u${userId}.v${version}`;

const isDismissed = (userId, version) => {
  if (userId == null || version == null) return false;
  try {
    return localStorage.getItem(dismissKey(userId, version)) !== null;
  } catch (error) {
    // Safari en modo privado lanza al tocar localStorage.
    console.error('No se pudo leer el descarte del comunicado:', error);
    return false;
  }
};

const markDismissed = (userId, version) => {
  if (userId == null || version == null) return;
  try {
    // Purga las versiones viejas de este usuario para dejar una sola clave.
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${KEY_PREFIX}.u${userId}.v`)) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem(dismissKey(userId, version), '1');
  } catch (error) {
    console.error('No se pudo guardar el descarte del comunicado:', error);
  }
};

export const AnnouncementProvider = ({ children }) => {
  const { token, user } = useContext(AuthContext);
  const [announcement, setAnnouncement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userId = user?.user_id;

  // Evita el doble fetch del doble montaje de StrictMode sin necesidad de AbortController.
  const fetchedForToken = useRef(null);

  const load = useCallback(async ({ autoOpen }) => {
    try {
      const data = await getActiveAnnouncement();
      setAnnouncement(data);
      if (autoOpen) {
        setIsModalOpen(Boolean(data) && !isDismissed(userId, data.version));
      }
    } catch (error) {
      // Un comunicado que falla nunca debe romper la app.
      console.error('No se pudo cargar el comunicado:', error);
      setAnnouncement(null);
    }
  }, [userId]);

  useEffect(() => {
    if (!token) {
      fetchedForToken.current = null;
      setAnnouncement(null);
      setIsModalOpen(false);
      return;
    }
    if (fetchedForToken.current === token) return;
    fetchedForToken.current = token;
    load({ autoOpen: true });
  }, [token, userId, load]);

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const dismiss = useCallback(() => {
    setIsModalOpen(false);
    if (announcement) markDismissed(userId, announcement.version);
  }, [announcement, userId]);

  // Refetch sin abrir el modal: lo usa la pantalla de admin tras guardar,
  // para que su propio banner refleje el estado real sin recargar.
  const refresh = useCallback(() => load({ autoOpen: false }), [load]);

  return (
    <AnnouncementContext.Provider value={{ announcement, isModalOpen, openModal, dismiss, refresh }}>
      {children}
    </AnnouncementContext.Provider>
  );
};

export default AnnouncementProvider;
